import { ProgressTracker } from './ProgressTracker';
import { LegendKeyCard } from './LegendKeyCard';
import { SectionLabel } from './SectionLabel';
import { CornerPieceCard } from './CornerPieceCard';
import { PieceCard } from './PieceCard';
import { CurriculumJourney } from './CurriculumJourney';

export function MapTableView({ corners, borders, pieces, onSelectPiece, studiedPieces, progressPct, isComplete, justStudied, studyGuide, guideLoading, showGuide, onToggleGuide, discount, tierInfo, activeTier, onChangeTier, showTierMenu, onToggleTierMenu, specialChapters, onOpenSpecial, onNavigate, selectedBook, theme, learningProgress, unlockedModules, getModuleKey, curriculumLessons, siteConfig, journeySteps }) {
  const innerPieces = pieces.filter(p => p.piece_type === 'inner');
  const TIER_LABELS = { beginner: 'Beginner', intermediate: 'Intermediate', berean: 'Berean', scholar: 'Scholar' };
  const TIER_COLORS_MAP = { beginner: '#d97706', intermediate: '#06b6d4', berean: '#8b5cf6', scholar: '#eab308' };

  // Corner piece data — use from puzzle piece data when available
  const getCornerData = (piece) => {
    return {
      ref: piece.scripture_ref || '',
      anchor: piece.theme_verse || '',
      question: piece.description || '',
      rulesOut: '',
    };
  };

  // Get legend key pieces from the loaded puzzle data
  const legendKeys = pieces.filter(p => p.piece_type === 'legend_key');

  // Check which legend keys are completed / unlocked
  const legendProgress = legendKeys.map((lk, i) => {
    const modKey = getModuleKey?.(lk.keyword) || lk.keyword;
    const done = learningProgress?.[modKey]?.status === 'completed';
    const prevDone = i === 0 ? true : (learningProgress?.[getModuleKey?.(legendKeys[i-1]?.keyword) || legendKeys[i-1]?.keyword]?.status === 'completed');
    return { ...lk, done, unlocked: i === 0 || prevDone };
  });
  const allKeysComplete = legendKeys.length > 0 && legendProgress.every(lk => lk.done);

  // Fallback for Book 2 hardcoded legend check (backward compat)
  const gitDone = learningProgress?.['GIT_101']?.status === 'completed';
  const bpUnlocked = unlockedModules?.includes('BLUEPRINTS');
  const bpDone = learningProgress?.['BLUEPRINTS']?.status === 'completed';
  const recapUnlocked = unlockedModules?.includes('RECAPITULATION');
  const recapDone = learningProgress?.['RECAPITULATION']?.status === 'completed';
  const allKeysCompleteBook2 = gitDone && bpDone && recapDone;

  return (
    <div className="max-w-5xl mx-auto" style={{ fontFamily: "'Times New Roman', 'Georgia', serif" }}>

      {/* LANDING INTRO / BRIEFING — Admin-editable via Site Config */}
      {siteConfig?.landing_intro_text && (
        <div className="px-4 pt-16 pb-12" data-testid="landing-intro">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ background: 'radial-gradient(circle, rgba(217,119,6,0.15), rgba(120,53,15,0.05))', border: '1.5px solid rgba(217,119,6,0.25)' }}>
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="rgba(217,119,6,0.7)" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-5" data-testid="landing-intro-heading"
              style={{ background: theme?.accentGradient || 'linear-gradient(180deg, #fcd34d 0%, #d97706 50%, #92400e 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {siteConfig.landing_intro_heading || 'Welcome, Seeker'}
            </h2>
            <p className="text-sm sm:text-base leading-[2] mb-8" data-testid="landing-intro-text"
              style={{ color: theme?.textSecondary || 'rgba(220,190,130,0.65)' }}>
              {siteConfig.landing_intro_text}
            </p>
            <button onClick={() => {
              const legend = document.getElementById('zone-2');
              if (legend) legend.scrollIntoView({ behavior: 'smooth' });
            }}
              data-testid="landing-intro-cta"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold transition-all duration-300 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #92400e, #d97706, #b45309)', color: '#fff', boxShadow: '0 4px 24px rgba(217,119,6,0.3)' }}>
              {siteConfig.landing_intro_cta || 'Begin the Journey'}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </button>
            <div className="mt-10 h-px w-48 mx-auto" style={{ background: 'linear-gradient(90deg, transparent, rgba(217,119,6,0.3), transparent)' }} />
          </div>
        </div>
      )}

      {/* TITLE + PROGRESS */}
      <div className="px-4 pt-12 pb-6" data-testid="zone-1-the-map">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.5em] uppercase mb-4" style={{ color: theme?.labelColor || '#dc2626' }}>THE TREASURE MAP</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
            style={{ background: theme?.accentGradient || 'linear-gradient(180deg, #fcd34d 0%, #d97706 50%, #92400e 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: "'Times New Roman', serif" }}>
            {selectedBook?.title || 'The Kings Return'}
          </h2>
          <p className="text-sm max-w-xl mx-auto leading-relaxed italic" style={{ color: theme?.textSecondary || 'rgba(220,190,130,0.5)' }}>
            "The experienced puzzle builder knows exactly what to do. She does four things — in order."
          </p>
        </div>

        <ProgressTracker journeySteps={journeySteps} learningProgress={learningProgress} onNavigate={onNavigate} theme={theme} />
      </div>

      {/* SECTION 1: THE LEGEND — Reading Keys (START HERE) */}
      <div id="zone-2" className="px-4 py-12" style={{ borderTop: `1px solid ${theme?.borderPrimary || 'rgba(120,80,20,0.12)'}` }} data-testid="zone-2-the-legend">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.5em] uppercase mb-4" style={{ color: theme?.labelColor || '#dc2626' }}>START HERE — THE LEGEND</p>
          <p className="text-lg font-bold mb-3" style={{ color: theme?.accentPrimary || '#d4a54a' }}>Every Treasure Map Has a Legend</p>
          <p className="text-sm max-w-xl mx-auto leading-relaxed italic" style={{ color: theme?.textSecondary || 'rgba(220,190,130,0.5)' }}>
            {legendKeys.length > 0 ? legendKeys[0]?.description || "Before we walk the map, you need these keys. Without them, the map looks like what you've already been taught. With them, it reads exactly as it was written." : "A key that tells you what the compass rose means, what the dotted line represents, what the X actually marks."}
          </p>
        </div>

        <div className="space-y-5 max-w-2xl mx-auto">
          {legendProgress.map((lk, i) => (
            <LegendKeyCard
              key={lk.piece_id}
              number={i + 1}
              title={lk.title}
              subtitle={lk.scripture_ref ? `${lk.scripture_ref}` : lk.keyword}
              description={lk.description || ''}
              status={lk.done ? 'completed' : lk.unlocked ? 'unlocked' : 'locked'}
              onStart={() => lk.unlocked ? onNavigate(`/lesson/${lk.keyword?.toLowerCase() || lk.piece_id}`) : null}
              buttonLabel={lk.done ? `Review ${lk.title}` : lk.unlocked ? `Begin ${lk.title}` : `Complete ${legendProgress[i-1]?.title || 'previous'} first`}
            />
          ))}
        </div>

        {(allKeysComplete || allKeysCompleteBook2) && (
          <div className="text-center mt-10 py-6 rounded-xl border" style={{ background: 'rgba(16,185,129,0.05)', borderColor: 'rgba(16,185,129,0.2)' }} data-testid="legend-complete">
            <p className="text-sm font-bold mb-2" style={{ color: '#10b981' }}>All Legend Keys are in place.</p>
            <p className="text-xs italic" style={{ color: 'rgba(16,185,129,0.6)' }}>The Corner Pieces are now unlocked. Continue below.</p>
          </div>
        )}
      </div>

      {/* SECTION 2: THE FOUR CORNER PIECES */}
      <div className="px-4 py-12" style={{ borderTop: `1px solid ${theme?.borderPrimary || 'rgba(120,80,20,0.12)'}` }} data-testid="zone-corner-pieces">
        <SectionLabel text="The Four Corner Pieces" subtitle="The anchors of the puzzle — these four scriptures lock the frame." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
          {corners.map((piece) => {
            const globalIdx = pieces.findIndex(p => p.piece_id === piece.piece_id);
            const mk = getModuleKey?.(piece);
            const cd = getCornerData(piece);
            const locked = mk ? !unlockedModules?.includes(mk) : false;
            const completed = mk ? learningProgress?.[mk]?.status === 'completed' : false;
            return (
              <CornerPieceCard key={piece.piece_id} piece={piece} cornerData={cd}
                onClick={() => {
                  if (!locked || completed) {
                    // Navigate based on piece data — use keyword for routing
                    const route = piece.piece_id.startsWith('vk-') 
                      ? `/ebooks/${piece.book_id}/${piece.number || 1}`
                      : `/lesson/corner${piece.number || piece.order}`;
                    onNavigate(route);
                  } else {
                    document.getElementById('zone-2')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }} index={globalIdx}
                isLocked={locked && !completed} isCompleted={completed}
                isStudied={studiedPieces.includes(piece.piece_id)} />
            );
          })}
        </div>

        {/* Summary Table */}
        <div className="mb-10 rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(30,58,95,0.3)' }} data-testid="corner-summary-table">
          <div className="px-5 py-3 text-center" style={{ background: 'rgba(30,58,95,0.12)' }}>
            <p className="text-sm font-bold tracking-wider" style={{ color: '#d4a54a' }}>THE FOUR CORNER PIECES AT A GLANCE</p>
          </div>
          <table className="w-full text-sm" style={{ color: 'rgba(220,190,130,0.8)' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(30,58,95,0.2)' }}>
                <th className="px-4 py-2 text-left text-xs" style={{ color: '#d4a54a' }}>#</th>
                <th className="px-4 py-2 text-left text-xs" style={{ color: '#d4a54a' }}>Passage</th>
                <th className="px-4 py-2 text-left text-xs hidden sm:table-cell" style={{ color: '#d4a54a' }}>Anchor Phrase</th>
                <th className="px-4 py-2 text-left text-xs hidden md:table-cell" style={{ color: '#d4a54a' }}>What It Rules Out</th>
              </tr>
            </thead>
            <tbody>
              {corners.map((piece, i) => {
                const cd = getCornerData(piece);
                return (
                  <tr key={piece.piece_id} style={{ borderBottom: '1px solid rgba(30,58,95,0.08)' }}>
                    <td className="px-4 py-3 font-bold" style={{ color: '#d4a54a' }}>#{i+1}</td>
                    <td className="px-4 py-3">{cd.ref}</td>
                    <td className="px-4 py-3 hidden sm:table-cell font-bold" style={{ color: '#d4a54a' }}>"{cd.anchor}"</td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs" style={{ color: '#dc2626' }}>{cd.rulesOut}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Corner Completion Celebration */}
        {(() => {
          const allCornersComplete = ['CORNER_1','CORNER_2','CORNER_3','CORNER_4'].every(
            k => learningProgress?.[k]?.status === 'completed'
          );
          if (!allCornersComplete) return null;
          return (
            <div className="mb-10 relative overflow-hidden rounded-2xl border-2" data-testid="corner-completion-celebration"
              style={{
                borderColor: 'rgba(212,165,74,0.4)',
                background: 'linear-gradient(135deg, rgba(20,15,5,0.95) 0%, rgba(30,22,8,0.98) 50%, rgba(20,15,5,0.95) 100%)',
              }}>
              <style>{`
                @keyframes celebrateShimmer {
                  0% { background-position: -300% center; }
                  100% { background-position: 300% center; }
                }
                @keyframes celebrateStar {
                  0%, 100% { opacity: 0.2; transform: scale(0.8) rotate(0deg); }
                  50% { opacity: 1; transform: scale(1.2) rotate(180deg); }
                }
              `}</style>
              {/* Shimmer overlay */}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: 'linear-gradient(90deg, transparent 35%, rgba(255,215,0,0.06) 50%, transparent 65%)',
                backgroundSize: '300% 100%',
                animation: 'celebrateShimmer 6s linear infinite',
              }} />
              {/* Corner ornaments */}
              {['top-0 left-0 border-t-2 border-l-2 rounded-tl-[14px]','top-0 right-0 border-t-2 border-r-2 rounded-tr-[14px]','bottom-0 left-0 border-b-2 border-l-2 rounded-bl-[14px]','bottom-0 right-0 border-b-2 border-r-2 rounded-br-[14px]'].map((pos,i) => (
                <div key={i} className={`absolute w-5 h-5 ${pos}`} style={{ borderColor: 'rgba(212,165,74,0.3)' }} />
              ))}
              <div className="relative px-6 py-8 text-center">
                {/* Stars */}
                <div className="flex justify-center gap-3 mb-4">
                  {[0,1,2,3].map(i => (
                    <svg key={i} className="w-5 h-5" viewBox="0 0 24 24" fill="#d4a54a" style={{ animation: `celebrateStar 2s ease-in-out ${i * 0.3}s infinite` }}>
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <p className="text-xs tracking-[0.5em] uppercase font-bold mb-2" style={{ color: '#d4a54a' }}>The Frame Is Anchored</p>
                <p className="text-lg font-serif font-bold mb-3" style={{ color: '#fcd34d' }}>
                  All Four Corner Pieces Are Set
                </p>
                <p className="text-sm max-w-lg mx-auto leading-relaxed mb-4" style={{ color: 'rgba(220,190,130,0.55)' }}>
                  The four anchoring scriptures are in place. No rapture theory can ignore these passages.
                  Now connect the corners with the six Border Pieces below.
                </p>
                <div className="h-px w-40 mx-auto" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,165,74,0.5), transparent)' }} />
              </div>
            </div>
          );
        })()}
      </div>

      {/* SECTION 3: THE JOURNEY — Borders, Warnings, Landmarks, Interior */}
      {curriculumLessons?.length > 0 && (
        <CurriculumJourney
          lessons={curriculumLessons}
          learningProgress={learningProgress}
          unlockedModules={unlockedModules}
          onNavigate={onNavigate}
          theme={theme}
        />
      )}

      {/* SECTION 4: THE INNER PIECES — Appendix Deep Dives */}
      {innerPieces.length > 0 && (
        <div className="px-4 py-12" style={{ borderTop: `1px solid ${theme?.borderPrimary || 'rgba(120,80,20,0.12)'}` }} data-testid="zone-4-inner-pieces">
          <div className="text-center mb-10">
            <p className="text-xs tracking-[0.5em] uppercase mb-4" style={{ color: theme?.labelColor || '#dc2626' }}>THE INNER PIECES — DEEPER EXPLORATION</p>
            <p className="text-lg font-bold mb-3" style={{ color: theme?.accentPrimary || '#d4a54a' }}>Fill In the Picture</p>
            <p className="text-sm max-w-xl mx-auto leading-relaxed italic" style={{ color: theme?.textSecondary || 'rgba(220,190,130,0.5)' }}>
              "The frame is set — corners locked, borders connected. Now the real picture emerges. Each inner piece is a deep-dive appendix that fills in the theology, the history, and the evidence."
            </p>
            <p className="text-xs mt-3" style={{ color: 'rgba(220,190,130,0.35)' }}>
              {innerPieces.length} appendix pieces — unlocked after completing the frame
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {innerPieces.map((piece) => {
              const globalIdx = pieces.findIndex(p => p.piece_id === piece.piece_id);
              const mk = getModuleKey?.(piece);
              return <PieceCard key={piece.piece_id} piece={piece} isCorner={false} onClick={() => onSelectPiece(globalIdx)} index={globalIdx} isStudied={studiedPieces.includes(piece.piece_id)} allPieces={pieces}
                moduleStatus={mk ? learningProgress?.[mk]?.status : null}
                isModuleLocked={mk ? !unlockedModules?.includes(mk) : false} />;
            })}
          </div>
        </div>
      )}

      {/* Buy CTA */}
      {onNavigate && (
        <div className="max-w-2xl mx-auto px-4 py-16" data-testid="map-bottom-buy-cta">
          <div className="relative rounded-2xl overflow-hidden border" style={{ borderColor: 'rgba(120,80,20,0.2)', background: 'linear-gradient(135deg, rgba(15,10,4,0.95), rgba(30,20,8,0.9), rgba(15,10,4,0.95))' }}>
            <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, #d97706 30%, #fcd34d 50%, #d97706 70%, transparent)' }} />
            <div className="px-8 py-10 text-center">
              <p className="text-[10px] tracking-[0.35em] uppercase font-mono mb-3" style={{ color: '#92400e' }}>Unlock the Full Treasure</p>
              <h3 className="text-xl sm:text-2xl font-bold mb-3" style={{ background: 'linear-gradient(180deg, #fcd34d, #d97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Own the Complete Book
              </h3>
              <p className="text-sm leading-relaxed mb-8 max-w-md mx-auto" style={{ color: 'rgba(220,190,130,0.45)' }}>
                Every puzzle piece, every Grammar Integrity Test, every verdict — the full journey from prologue to closing chapter.
              </p>
              <button onClick={() => onNavigate('/buy')} data-testid="btn-map-bottom-buy"
                className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full text-base font-semibold transition-all duration-300 hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #92400e, #d97706, #b45309)', color: '#fff', boxShadow: '0 4px 24px rgba(217,119,6,0.3)' }}>
                Buy the Book
              </button>
              <div className="mt-5">
                <button onClick={() => onNavigate('/subscriptions')} data-testid="btn-map-bottom-subscribe"
                  className="text-[11px] hover:text-amber-400 transition-colors underline decoration-amber-800/30 underline-offset-2" style={{ color: 'rgba(180,140,70,0.5)' }}>
                  Or become a member for full access
                </button>
              </div>
            </div>
            <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, #92400e 30%, #d97706 50%, #92400e 70%, transparent)' }} />
          </div>
        </div>
      )}
    </div>
  );
}
